"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
const services = [{
  id: "01",
  title: "Full Bathroom Remodels",
  description: "Request a quote for a full bathroom remodel that may include showers, tubs, tile, flooring, vanities, fixtures, lighting, storage, and layout updates.",
  href: "/contact/#request-your-bathroom-remodel-quote",
  linkLabel: "Request a Remodel Quote"
}, {
  id: "02",
  title: "Shower Remodels",
  description: "Shower remodels can include walk-in showers, tile showers, glass doors, shower fixtures, storage niches, and improved layout or accessibility.",
  href: "/shower-remodel-lakeland-fl/",
  linkLabel: "View Shower Remodels"
}, {
  id: "03",
  title: "Tub and Shower Updates",
  description: "Tub replacement projects can include replacing an old bathtub, updating a tub and shower combination, or reviewing tub-to-shower conversion options.",
  href: "/tub-replacement-lakeland-fl/",
  linkLabel: "View Tub Replacement"
}, {
  id: "04",
  title: "Tile and Flooring",
  description: "Bathroom tile and flooring updates can improve the look, durability, water resistance, and daily use of the space.",
  href: "/services/",
  linkLabel: "View Services"
}, {
  id: "05",
  title: "Vanities and Fixtures",
  description: "Vanity and fixture updates can include sinks, faucets, mirrors, countertops, lighting, hardware, and storage improvements.",
  href: "/contact/#request-your-bathroom-remodel-quote",
  linkLabel: "Request a Quote"
}, {
  id: "06",
  title: "Layout and Storage Updates",
  description: "Bathroom layout and storage updates can help make the room more useful, comfortable, and easier to use day to day.",
  href: "/small-bathroom-remodel-lakeland-fl/",
  linkLabel: "View Small Bathroom Remodels"
}];
const relatedLinks = [{
  label: "Bathroom Remodel Cost in Lakeland, FL",
  href: "/bathroom-remodel-cost-in-lakeland-fl/"
}, {
  label: "Shower Remodel Lakeland FL",
  href: "/shower-remodel-lakeland-fl/"
}, {
  label: "Tub Replacement Lakeland FL",
  href: "/tub-replacement-lakeland-fl/"
}, {
  label: "Small Bathroom Remodel Lakeland FL",
  href: "/small-bathroom-remodel-lakeland-fl/"
}, {
  label: "Bathroom Renovation Lakeland FL",
  href: "/bathroom-renovation-lakeland-fl/"
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};
export default function BathroomRemodelServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="bathroomremodelservices" className="bg-background py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        <div ref={headerRef} className={`max-w-3xl mb-16 md:mb-24 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h2 className="text-3xl md:text-5xl font-bold font-default text-foreground tracking-tight mb-8">
            Bathroom Remodel Services in Lakeland, FL
          </h2>

          <div className="h-[2px] w-24 bg-secondary mb-8" />

          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Planning a bathroom remodel in Lakeland, FL can include anything
            from a shower or vanity update to a full bathroom renovation.
            Lakeland Bath Remodel Pros helps homeowners request quotes for
            bathroom updates, shower remodels, tub replacements, tile, flooring,
            vanities, fixtures, lighting, storage, and layout improvements.
          </p>
        </div>

        <motion.div ref={gridRef} variants={containerVariants} initial="hidden" animate={isGridVisible ? "show" : "hidden"} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => <motion.div key={service.id} data-index={index} variants={itemVariants} whileHover={{
          y: -8
        }} transition={{
          duration: 0.3,
          ease: "easeOut"
        }} className="h-full">
              <Card className="h-full bg-primary text-primary-foreground border-primary/20 rounded-lg shadow-xl flex flex-col">
                <CardHeader className="pb-4">
                  <div className="font-mono text-xs tracking-widest text-secondary mb-4 uppercase opacity-90">
                    {service.id}
                  </div>

                  <CardTitle className="text-2xl font-default font-semibold tracking-tight">
                    {service.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex flex-col flex-grow">
                  <CardDescription className="text-primary-foreground/70 text-base leading-relaxed">
                    {service.description}
                  </CardDescription>

                  <div className="mt-auto pt-8">
                    <Link to={service.href} className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-primary-foreground hover:text-secondary transition-colors">
                      <span className="relative">
                        {service.linkLabel}
                        <span className="absolute left-0 -bottom-1 h-px w-0 bg-secondary transition-all duration-300 group-hover:w-full" />
                      </span>
                      <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 md:mt-20 rounded-lg border border-border bg-card p-6 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            <div className="lg:col-span-5">
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-primary mb-4">
                Related Bathroom Remodel Resources
              </p>

              <h3 className="font-default text-2xl md:text-3xl font-bold text-foreground leading-tight">
                More bathroom remodel information for Lakeland, FL.
              </h3>
            </div>

            <div className="lg:col-span-7">
              <p className="text-base text-muted-foreground leading-relaxed mb-6">
                Review related bathroom remodeling pages for cost factors,
                shower updates, tub replacement, small bathroom layouts, and
                bathroom renovation options in Lakeland, FL.
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relatedLinks.map((link, i) => <li key={link.href} data-index={i}>
                    <Link to={link.href} className="group inline-flex items-center gap-2 text-foreground hover:text-primary transition-colors">
                      <ArrowRight className="size-4 text-primary transition-transform duration-300 group-hover:translate-x-1" />
                      <span className="underline underline-offset-4">
                        {link.label}
                      </span>
                    </Link>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}